import { useCallback, useMemo } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { styled, type Theme } from '@mui/material/styles'

import { getItemTypeLabel } from 'src/state/items'
import { useAppStore } from 'src/state/store'
import InlineText from '../ui/InlineText'
import { getIcon } from '../Icons'
import type { AnySearchable } from './types'
import { getName, isSearchableStandardItem } from './utils'


const AutocompleteOption = styled('div')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  minWidth: 0,
  padding: theme.spacing(1, 0),
  lineHeight: 1,
}))
const OptionIconHolder = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(2.5),
}))
const OptionName = styled(InlineText)(() => ({
  flexGrow: 1,
  minWidth: 0,
  lineHeight: 1,
}))

export default function OptionComponent({
  option,
  showDescription,
  showGroupMemberCount,
  showIcon,
  showCheckbox,
  selected,
}: {
  option: AnySearchable,
  showDescription: boolean,
  showGroupMemberCount: boolean,
  showIcon: boolean,
  showCheckbox: boolean,
  selected: boolean,
}) {
  const icon = getIcon(option.type)
  const name = getName(option)
  const item = isSearchableStandardItem(option) ? option.data : undefined
  const itemsMap = useAppStore(state => state.items)

  const groupMembersText = useMemo(
    () => {
      if (item && item.type === 'group') {
        const count = item.members.filter(id => {
          const m = itemsMap[id]
          return !m || (!m.archived && !m.deleted)
        }).length
        const s = count !== 1 ? 's' : ''
        return ` (${count} member${s})`
      }
      return ''
    },
    [item, itemsMap],
  )
  const clippedDescription = useMemo(
    () => {
      if (item && typeof item.description === 'string') {
        const base = item.description
        const clipped = base.slice(0, 100)
        if (clipped.length < base.length) {
          const clippedToWord = clipped.slice(0, clipped.lastIndexOf(' '))
          return `${clippedToWord}…`
        }
        return base
      }
      return null
    },
    [item],
  )

  const getFontSize = useCallback(
    (theme: Theme) => theme.typography.caption.fontSize,
    [],
  )

  return (
    <>
      {option.dividerBefore && <Divider />}
      <AutocompleteOption>
        {showCheckbox && (
          <OptionIconHolder>
            <Checkbox size="small" checked={!!selected} tabIndex={-1} disableRipple />
          </OptionIconHolder>
        )}
        {showIcon && (
          <OptionIconHolder>
            {icon}
          </OptionIconHolder>
        )}

        {option.create ? (
          <div>
            <span>Add {getItemTypeLabel(option.type).toLowerCase()} </span>
            <Typography sx={{ fontWeight: 500 }}>
              {name}
            </Typography>
          </div>
        ) : (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <OptionName noWrap>
                {name}
              </OptionName>

              <InlineText
                color="text.secondary"
                sx={{ fontWeight: 300, whiteSpace: 'pre' }}
              >
                {showGroupMemberCount && option.type === 'group' ? groupMembersText : ''}
              </InlineText>
            </Typography>

            {showDescription && clippedDescription && (
              <OptionName
                color="text.secondary"
                noWrap
                sx={{ fontSize: getFontSize }}
              >
                {clippedDescription}
              </OptionName>
            )}
          </Box>
        )}
      </AutocompleteOption>
    </>
  )
}
